import React, { useState, useEffect, useCallback } from 'react'
import { supabase } from '../supabaseClient'

function fmtSize(bytes) {
  if (!bytes) return '—'
  if (bytes < 1024*1024) return `${(bytes/1024).toFixed(1)} KB`
  return `${(bytes/(1024*1024)).toFixed(1)} MB`
}
function fmtDuration(mins) {
  if (!mins) return '—'
  if (mins < 60) return `${mins}m`
  return `${Math.floor(mins/60)}h ${mins%60}m`
}
function fmtDate(ts) {
  if (!ts) return '—'
  return new Date(ts).toLocaleString('en-IN', {
    day:'2-digit', month:'short', year:'numeric',
    hour:'2-digit', minute:'2-digit', hour12:true,
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  })
}

// Meeting ID from a class link: Zoom (zoom.us/j/<digits>) or Google Meet
// (meet.google.com/<abc-defg-hij>). Must match extractMeetingId in
// api/meetings.js.
function meetingIdFromLink(link) {
  const zoom = link?.match(/\/j\/(\d+)/)
  if (zoom) return zoom[1]
  const meet = link?.match(/meet\.google\.com\/([a-z]{3}-[a-z]{4}-[a-z]{3})/i)
  return meet ? meet[1].toLowerCase() : null
}

function withPwd(url, password) {
  if (!url || !password) return url
  try {
    const u = new URL(url)
    if (!u.searchParams.has('pwd')) u.searchParams.set('pwd', password)
    return u.toString()
  } catch { return url }
}

const FILE_TYPE_STYLE = {
  MP4:        { bg:'rgba(30,144,255,0.15)',  color:'#5aabff',  icon:'🎬', label:'Video'      },
  M4A:        { bg:'rgba(139,92,246,0.15)',  color:'#a78bfa',  icon:'🎵', label:'Audio'      },
  CHAT:       { bg:'rgba(244,163,53,0.15)',  color:'#f4a335',  icon:'💬', label:'Chat'       },
  TRANSCRIPT: { bg:'rgba(16,185,129,0.15)',  color:'#34d399',  icon:'📝', label:'Transcript' },
}

async function fetchAllRecordings(from, to) {
  const { data: { session } } = await supabase.auth.getSession()
  const res  = await fetch(`/api/meetings?action=recordings&from=${from}&to=${to}`, {
    headers: { Authorization: `Bearer ${session?.access_token || ''}` },
  })
  const data = await res.json().catch(() => ({}))
  if (!res.ok || data?.error) throw new Error(data?.error || `Failed to load recordings (${res.status})`)
  return data?.recordings || []
}

function RecordingCard({ recording, isHero = false }) {
  const pwd       = recording.password
  const videoFile = recording.files?.find(f => f.file_type === 'MP4')
  const audioFile = recording.files?.find(f => f.file_type === 'M4A')
  const chatFile  = recording.files?.find(f => f.file_type === 'CHAT')
  const transFile = recording.files?.find(f => f.file_type === 'TRANSCRIPT')
  // Google Meet: Gemini "Take notes for me" doc and transcript doc.
  const notesUrl      = recording.notes_url
  const transcriptUrl = recording.transcript_url || transFile?.download_url
  const [open, setOpen] = useState(false)

  if (isHero) return (
    <div style={{ background:'linear-gradient(135deg,rgba(30,144,255,0.12),rgba(139,92,246,0.08))', border:'1px solid rgba(30,144,255,0.3)', borderRadius:'16px', overflow:'hidden', marginBottom:'20px' }}>
      <div style={{ display:'flex', alignItems:'center', gap:'10px', padding:'12px 18px', background:'rgba(30,144,255,0.1)', borderBottom:'0.5px solid rgba(30,144,255,0.2)' }}>
        <div style={{ width:'8px', height:'8px', borderRadius:'50%', background:'#ef4444', boxShadow:'0 0 6px #ef4444', animation:'pulse 1.5s infinite' }}/>
        <span style={{ fontSize:'11px', fontWeight:800, color:'#5aabff', letterSpacing:'0.1em', textTransform:'uppercase' }}>Latest Class Recording</span>
        <span style={{ marginLeft:'auto', fontSize:'11px', color:'rgba(var(--fg-rgb),var(--a35))' }}>{fmtDate(recording.start_time)}</span>
      </div>
      <div style={{ padding:'18px' }}>
        <div style={{ fontSize:'17px', fontWeight:800, color:'var(--fg-solid)', marginBottom:'6px' }}>{recording.topic || 'Class Recording'}</div>
        <div style={{ display:'flex', gap:'14px', marginBottom:'16px', flexWrap:'wrap' }}>
          <span style={{ fontSize:'12px', color:'rgba(var(--fg-rgb),var(--a40))' }}>⏱ {fmtDuration(recording.duration)}</span>
          <span style={{ fontSize:'12px', color:'rgba(var(--fg-rgb),var(--a40))' }}>💾 {fmtSize(recording.total_size)}</span>
          <span style={{ fontSize:'12px', color:'rgba(var(--fg-rgb),var(--a40))' }}>📁 {recording.files?.length||0} files</span>
        </div>
        <div style={{ display:'flex', gap:'10px', flexWrap:'wrap', marginBottom:'12px' }}>
          {videoFile?.play_url ? (
            <a href={withPwd(videoFile.play_url, pwd)} target="_blank" rel="noreferrer"
              style={{ display:'flex', alignItems:'center', gap:'8px', padding:'12px 24px', background:'linear-gradient(135deg,#1e90ff,#0ea5e9)', color:'#fff', borderRadius:'12px', textDecoration:'none', fontWeight:800, fontSize:'14px' }}>
              ▶ Watch Recording
            </a>
          ) : (
            <div style={{ padding:'12px 24px', background:'rgba(var(--fg-rgb),0.06)', color:'rgba(var(--fg-rgb),var(--a30))', borderRadius:'12px', fontSize:'13px' }}>{recording.files?.length ? 'Video processing...' : 'No video for this class'}</div>
          )}
          {notesUrl && (
            <a href={notesUrl} target="_blank" rel="noreferrer"
              style={{ display:'flex', alignItems:'center', gap:'8px', padding:'12px 20px', background:'rgba(244,163,53,0.12)', color:'#f4a335', border:'0.5px solid rgba(244,163,53,0.3)', borderRadius:'12px', textDecoration:'none', fontWeight:700, fontSize:'13px' }}>
              📝 Class Notes
            </a>
          )}
          {videoFile?.download_url && (
            <a href={withPwd(videoFile.download_url, pwd)} target="_blank" rel="noreferrer"
              style={{ display:'flex', alignItems:'center', gap:'8px', padding:'12px 20px', background:'rgba(16,185,129,0.12)', color:'#34d399', border:'0.5px solid rgba(16,185,129,0.25)', borderRadius:'12px', textDecoration:'none', fontWeight:700, fontSize:'13px' }}>
              ↓ Download
            </a>
          )}
          {audioFile?.play_url && (
            <a href={withPwd(audioFile.play_url, pwd)} target="_blank" rel="noreferrer"
              style={{ display:'flex', alignItems:'center', gap:'8px', padding:'12px 20px', background:'rgba(139,92,246,0.12)', color:'#a78bfa', border:'0.5px solid rgba(139,92,246,0.25)', borderRadius:'12px', textDecoration:'none', fontWeight:700, fontSize:'13px' }}>
              🎵 Audio Only
            </a>
          )}
        </div>
        <div style={{ display:'flex', gap:'8px', flexWrap:'wrap' }}>
          {chatFile?.download_url  && <a href={withPwd(chatFile.download_url,  pwd)} target="_blank" rel="noreferrer" style={{ fontSize:'11px', fontWeight:700, padding:'5px 12px', borderRadius:'8px', background:'rgba(244,163,53,0.1)',  color:'#f4a335', border:'0.5px solid rgba(244,163,53,0.2)',  textDecoration:'none' }}>💬 Chat Log</a>}
          {transcriptUrl && <a href={transFile?.download_url === transcriptUrl ? withPwd(transcriptUrl, pwd) : transcriptUrl} target="_blank" rel="noreferrer" style={{ fontSize:'11px', fontWeight:700, padding:'5px 12px', borderRadius:'8px', background:'rgba(16,185,129,0.1)', color:'#34d399', border:'0.5px solid rgba(16,185,129,0.2)', textDecoration:'none' }}>📄 Transcript</a>}
        </div>
      </div>
    </div>
  )

  return (
    <div style={{ background:'rgba(var(--fg-rgb),0.03)', border:'0.5px solid rgba(var(--fg-rgb),0.07)', borderRadius:'12px', overflow:'hidden' }}>
      <div style={{ display:'flex', alignItems:'center', gap:'12px', padding:'12px 16px', cursor:'pointer', flexWrap:'wrap' }} onClick={() => setOpen(o => !o)}>
        <div style={{ width:'40px', height:'32px', borderRadius:'6px', background:'rgba(30,144,255,0.1)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'16px', flexShrink:0 }}>🎬</div>
        <div style={{ flex:1, minWidth:'120px' }}>
          <div style={{ fontSize:'13px', fontWeight:600, color:'rgba(var(--fg-rgb),var(--a80))', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{recording.topic||'Class Recording'}</div>
          <div style={{ fontSize:'11px', color:'rgba(var(--fg-rgb),var(--a30))', marginTop:'2px' }}>{fmtDate(recording.start_time)} · {fmtDuration(recording.duration)} · {fmtSize(recording.total_size)}</div>
        </div>
        {videoFile?.play_url && (
          <a href={withPwd(videoFile.play_url, pwd)} target="_blank" rel="noreferrer" onClick={e=>e.stopPropagation()}
            style={{ fontSize:'11px', fontWeight:700, padding:'6px 14px', borderRadius:'8px', background:'rgba(30,144,255,0.15)', color:'#5aabff', border:'0.5px solid rgba(30,144,255,0.2)', textDecoration:'none', flexShrink:0 }}>
            ▶ Watch
          </a>
        )}
        {notesUrl && (
          <a href={notesUrl} target="_blank" rel="noreferrer" onClick={e=>e.stopPropagation()}
            style={{ fontSize:'11px', fontWeight:700, padding:'6px 14px', borderRadius:'8px', background:'rgba(244,163,53,0.12)', color:'#f4a335', border:'0.5px solid rgba(244,163,53,0.25)', textDecoration:'none', flexShrink:0 }}>
            📝 Notes
          </a>
        )}
        <span style={{ fontSize:'13px', color:'rgba(var(--fg-rgb),var(--a25))', flexShrink:0, transform:open?'rotate(180deg)':'none', transition:'transform 0.2s' }}>▾</span>
      </div>
      {open && (
        <div style={{ borderTop:'0.5px solid rgba(var(--fg-rgb),0.05)', padding:'10px 16px', display:'grid', gap:'6px' }}>
          {recording.transcript_url && (
            <a href={recording.transcript_url} target="_blank" rel="noreferrer" style={{fontSize:'11px',fontWeight:700,padding:'6px 10px',borderRadius:'7px',background:'rgba(16,185,129,0.1)',color:'#34d399',textDecoration:'none',justifySelf:'start'}}>📄 Transcript</a>
          )}
          {(recording.files||[]).filter(f => f.file_type !== 'TIMELINE').map((f,fi) => {
            const ft = FILE_TYPE_STYLE[f.file_type] || FILE_TYPE_STYLE.MP4
            return (
              <div key={fi} style={{ display:'flex', alignItems:'center', gap:'10px', padding:'8px 10px', background:'rgba(var(--fg-rgb),0.02)', borderRadius:'8px', flexWrap:'wrap' }}>
                <span style={{fontSize:'15px'}}>{ft.icon}</span>
                <span style={{fontSize:'11px',fontWeight:700,padding:'2px 8px',borderRadius:'5px',background:ft.bg,color:ft.color}}>{ft.label}</span>
                <span style={{fontSize:'11px',color:'rgba(var(--fg-rgb),var(--a30))',flex:1}}>{fmtSize(f.file_size)}</span>
                <div style={{display:'flex',gap:'6px'}}>
                  {f.play_url     && <a href={withPwd(f.play_url,     pwd)} target="_blank" rel="noreferrer" style={{fontSize:'11px',fontWeight:700,padding:'4px 10px',borderRadius:'7px',background:'rgba(30,144,255,0.12)',color:'#5aabff',textDecoration:'none'}}>▶ Play</a>}
                  {f.download_url && <a href={withPwd(f.download_url, pwd)} target="_blank" rel="noreferrer" style={{fontSize:'11px',fontWeight:700,padding:'4px 10px',borderRadius:'7px',background:'rgba(16,185,129,0.1)',color:'#34d399',textDecoration:'none'}}>↓ Download</a>}
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

export default function ZoomRecordings({ profile }) {
  const [recordings, setRecordings] = useState([])
  const [loading,    setLoading]    = useState(true)
  const [error,      setError]      = useState('')
  const [dateFrom,   setDateFrom]   = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 90)
    return d.toISOString().slice(0,10)
  })
  const [dateTo,  setDateTo]  = useState(new Date().toISOString().slice(0,10))
  const [search,  setSearch]  = useState('')

  const isAdmin = ['admin', 'sales'].includes(profile.role)
  const [diag,        setDiag]        = useState(null)
  const [diagLoading, setDiagLoading] = useState(false)

  // Admin-only: asks Google (and Zoom, for past classes) why recordings may be missing (plan, settings,
  // app permissions, recent recordings) and lists the findings.
  async function runRecordingCheck() {
    setDiagLoading(true)
    try {
      const { data: { session } } = await supabase.auth.getSession()
      const res = await fetch('/api/meetings?diag=recordings', {
        headers: { Authorization: `Bearer ${session?.access_token || ''}` },
      })
      const data = await res.json().catch(() => ({}))
      setDiag(res.ok ? data.findings || [] : [{ ok: false, label: 'Recording check', detail: data.error || `Request failed (${res.status})` }])
    } catch (e) {
      setDiag([{ ok: false, label: 'Recording check', detail: e.message }])
    }
    setDiagLoading(false)
  }

  const loadRecordings = useCallback(async () => {
    setLoading(true); setError('')
    try {
      const allRecs = await fetchAllRecordings(dateFrom, dateTo)
      const sorted  = allRecs.sort((a,b) => new Date(b.start_time) - new Date(a.start_time))

      if (isAdmin) {
        // ── Admin: sees ALL recordings ────────────────────────────────────────
        setRecordings(sorted)

      } else if (profile.role === 'teacher') {
        // ── Teacher: only sees recordings for classes assigned to them ────────
        // FIX: no fallback to all — if no match, show empty
        const { data: cls } = await supabase
          .from('classes')
          .select('title, batch, class_date, meet_link')
          .eq('teacher_id', profile.id)
          .lte('class_date', new Date().toISOString().slice(0,10))

        const myClasses = cls || []

        // Match recordings only through the class's linked meeting ID.
        const myMeetingIds = new Set(
          myClasses
            .map(c => {
              // Extract meeting ID from zoom URL
              return meetingIdFromLink(c.meet_link)
            })
            .filter(Boolean)
        )
        const filtered = sorted.filter(r => {
          const recMeeting = String(r.meeting_id || '')
          return myMeetingIds.has(recMeeting)
        })

        // FIX: never show all recordings as fallback — teacher only sees their own
        setRecordings(filtered)

      } else if (profile.role === 'student') {
        // ── Student: only sees recordings from their enrolled classes ─────────
        // FIX: strict matching — only classes the student is enrolled in
        const { data: enr } = await supabase
          .from('enrollments')
          .select('class_id')
          .eq('student_id', profile.id)

        const classIds = (enr || []).map(e => e.class_id).filter(Boolean)

        if (!classIds.length) {
          setRecordings([])
          setLoading(false)
          return
        }

        const { data: cls } = await supabase
          .from('classes')
          .select('title, batch, class_date, meet_link')
          .in('id', classIds)
          .lte('class_date', new Date().toISOString().slice(0,10))

        const myClasses = cls || []

        // Strict matching by meeting ID from the enrolled class's link.
        const myMeetingIds = new Set(
          myClasses
            .map(c => {
              return meetingIdFromLink(c.meet_link)
            })
            .filter(Boolean)
        )

        const studentRecs = sorted.filter(r => {
          const recMeeting = String(r.meeting_id || '')
          return myMeetingIds.has(recMeeting)
        })

        setRecordings(studentRecs)

      } else {
        setRecordings(sorted)
      }
    } catch(e) {
      setError('Could not load recordings: ' + e.message)
    }
    setLoading(false)
  }, [profile.id, profile.role, dateFrom, dateTo, isAdmin])

  useEffect(() => { loadRecordings() }, [loadRecordings])

  const heroRec     = recordings.find(r => r.files?.some(f => f.file_type==='MP4' && f.play_url)) || recordings[0]
  const olderRecs   = recordings.filter(r => r !== heroRec)
  const filteredOld = olderRecs.filter(r => !search || r.topic?.toLowerCase().includes(search.toLowerCase()))

  const inp = { background:'var(--bg-input)', border:'0.5px solid rgba(var(--fg-rgb),0.1)', borderRadius:'10px', padding:'8px 12px', fontSize:'13px', color:'rgba(var(--fg-rgb),var(--a85))', outline:'none', fontFamily:'inherit', colorScheme:'dark' }

  return (
    <div id="uniedd-recordings" style={{ marginTop:'14px' }}>
      <div style={{ fontSize:'16px', fontWeight:700, color:'rgba(var(--fg-rgb),var(--a85))', marginBottom:'4px' }}>🎬 Class Recordings</div>
      <div style={{ fontSize:'12px', color:'rgba(var(--fg-rgb),var(--a30))', marginBottom:'12px' }}>
        {profile.role==='student' ? 'Recordings from your enrolled classes only' :
         profile.role==='teacher' ? 'Recordings from your assigned classes only' :
         'All class recordings (Google Meet and past Zoom classes)'}
      </div>

      {/* Access note for students/teachers */}
      {profile.role !== 'admin' && profile.role !== 'sales' && (
        <div style={{ padding:'8px 14px', background:'rgba(30,144,255,0.06)', border:'0.5px solid rgba(30,144,255,0.15)', borderRadius:'9px', fontSize:'11px', color:'rgba(var(--fg-rgb),var(--a40))', marginBottom:'12px' }}>
          🔒 {profile.role === 'student'
            ? 'You can only see recordings from classes you are enrolled in.'
            : 'You can only see recordings from classes assigned to you.'}
        </div>
      )}

      <div style={{ display:'flex', gap:'8px', marginBottom:'14px', flexWrap:'wrap', alignItems:'center' }}>
        <span style={{ fontSize:'12px', color:'rgba(var(--fg-rgb),var(--a30))' }}>From</span>
        <input type="date" value={dateFrom} onChange={e=>setDateFrom(e.target.value)} style={inp}/>
        <span style={{ fontSize:'12px', color:'rgba(var(--fg-rgb),var(--a30))' }}>to</span>
        <input type="date" value={dateTo} onChange={e=>setDateTo(e.target.value)} style={inp}/>
        <button onClick={loadRecordings} style={{ padding:'8px 16px', borderRadius:'9px', background:'rgba(30,144,255,0.15)', color:'#5aabff', border:'none', cursor:'pointer', fontSize:'13px', fontFamily:'inherit', fontWeight:700 }}>🔍 Search</button>
        {profile.role === 'admin' && (
          <button onClick={runRecordingCheck} disabled={diagLoading} style={{ padding:'8px 14px', borderRadius:'9px', background:'rgba(var(--fg-rgb),0.06)', color:'rgba(var(--fg-rgb),var(--a60))', border:'none', cursor: diagLoading ? 'default' : 'pointer', fontSize:'12px', fontFamily:'inherit', fontWeight:600 }}>
            {diagLoading ? 'Checking…' : '🩺 Check recording setup'}
          </button>
        )}
      </div>

      {diag && (
        <div style={{ padding:'12px 16px', marginBottom:'14px', background:'rgba(var(--fg-rgb),0.03)', border:'0.5px solid rgba(var(--fg-rgb),0.1)', borderRadius:'12px', display:'grid', gap:'8px' }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
            <div style={{ fontSize:'13px', fontWeight:700, color:'rgba(var(--fg-rgb),var(--a80))' }}>Recording setup</div>
            <button onClick={() => setDiag(null)} aria-label="Close" style={{ background:'none', border:'none', cursor:'pointer', color:'rgba(var(--fg-rgb),var(--a40))', fontSize:'14px' }}>✕</button>
          </div>
          {diag.map((f, i) => (
            <div key={i} style={{ display:'flex', gap:'10px', fontSize:'12px', lineHeight:1.5 }}>
              <span style={{ flexShrink:0 }}>{f.ok ? '✅' : '❌'}</span>
              <span><strong style={{ color:'rgba(var(--fg-rgb),var(--a80))' }}>{f.label}:</strong> <span style={{ color:'rgba(var(--fg-rgb),var(--a50))' }}>{f.detail}</span></span>
            </div>
          ))}
        </div>
      )}

      {loading ? (
        <div style={{ textAlign:'center', padding:'4rem', color:'rgba(var(--fg-rgb),var(--a20))', fontSize:'13px' }}>
          <div style={{ display:'inline-block', width:'28px', height:'28px', border:'2.5px solid rgba(var(--fg-rgb),0.08)', borderTopColor:'#1e90ff', borderRadius:'50%', animation:'spin .7s linear infinite', marginBottom:'14px' }}/>
          <div>Loading recordings...</div>
        </div>
      ) : error ? (
        <div style={{ padding:'12px 16px', background:'rgba(239,68,68,0.1)', border:'0.5px solid rgba(239,68,68,0.3)', borderRadius:'10px', fontSize:'13px', color:'#f87171' }}>⚠ {error}</div>
      ) : recordings.length === 0 ? (
        <div style={{ textAlign:'center', padding:'4rem 1rem', background:'rgba(var(--fg-rgb),0.03)', borderRadius:'14px', border:'0.5px solid rgba(var(--fg-rgb),0.07)' }}>
          <div style={{ fontSize:'44px', marginBottom:'12px' }}>🎬</div>
          <div style={{ fontSize:'14px', color:'rgba(var(--fg-rgb),var(--a40))', fontWeight:600 }}>No recordings found</div>
          <div style={{ fontSize:'12px', color:'rgba(var(--fg-rgb),var(--a20))', marginTop:'6px', lineHeight:1.6 }}>
            {profile.role === 'student'
              ? 'Recordings from your classes will appear here after sessions end.'
              : profile.role === 'teacher'
              ? 'Recordings from your assigned classes will appear here.'
              : 'Recordings appear a little while after classes end. Try adjusting the date range.'}
          </div>
        </div>
      ) : (
        <>
          {heroRec && <RecordingCard recording={heroRec} isHero/>}
          {olderRecs.length > 0 && (
            <>
              <div style={{ display:'flex', gap:'10px', marginBottom:'12px', flexWrap:'wrap', alignItems:'center' }}>
                <div style={{ fontSize:'13px', fontWeight:700, color:'rgba(var(--fg-rgb),var(--a50))', flex:1 }}>Previous Recordings ({olderRecs.length})</div>
                <input style={{...inp, width:'200px'}} type="text" placeholder="🔍 Search by title..." value={search} onChange={e=>setSearch(e.target.value)}/>
                <button onClick={loadRecordings} style={{ padding:'8px 14px', borderRadius:'9px', background:'rgba(var(--fg-rgb),0.06)', color:'rgba(var(--fg-rgb),var(--a40))', border:'none', cursor:'pointer', fontSize:'12px', fontFamily:'inherit' }}>↻ Refresh</button>
              </div>
              <div style={{ display:'grid', gap:'8px' }}>
                {filteredOld.length === 0
                  ? <div style={{ textAlign:'center', padding:'2rem', color:'rgba(var(--fg-rgb),var(--a30))', fontSize:'13px' }}>No recordings match your search.</div>
                  : filteredOld.map((rec, i) => <RecordingCard key={rec.meeting_id||i} recording={rec}/>)
                }
              </div>
            </>
          )}
        </>
      )}
      <style>{`@keyframes spin{to{transform:rotate(360deg)}} @keyframes pulse{0%,100%{opacity:1}50%{opacity:0.4}}`}</style>
    </div>
  )
}
